import { useState } from 'react';
import { Download, Copy, Share2, MoreVertical, Link2, Loader2, Check } from 'lucide-react';
import { logger } from '@/utils/logger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { downloadAIChatPDF } from './AIChatPDF';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
}

interface AIChatExportMenuProps {
  messages: Message[];
  companyName?: string;
  conversationTitle?: string;
}

const generateShareToken = () => {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

const formatMessagesAsText = (messages: Message[]): string => {
  return messages
    .map((msg) => {
      const role = msg.role === 'user' ? 'You' : 'AI Assistant';
      const time = new Date(msg.createdAt).toLocaleString();
      return `[${role}] (${time})\n${msg.content}`;
    })
    .join('\n\n---\n\n');
};

export function AIChatExportMenu({ messages, companyName, conversationTitle }: AIChatExportMenuProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const handleExportPDF = async () => {
    if (messages.length === 0) {
      toast({
        title: 'No messages to export',
        description: 'Start a conversation first.',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);
    try {
      await downloadAIChatPDF(messages, conversationTitle || 'AI Assistant Conversation', companyName);
      toast({
        title: 'PDF exported',
        description: 'Your conversation has been downloaded.',
      });
    } catch (error) {
      logger.error('PDF export error:', error);
      toast({
        title: 'Export failed',
        description: 'Could not generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyConversation = async () => {
    if (messages.length === 0) {
      toast({
        title: 'No messages to copy',
        description: 'Start a conversation first.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const text = formatMessagesAsText(messages);
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied to clipboard',
        description: 'Conversation text has been copied.',
      });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Could not copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateShareLink = async () => {
    if (messages.length === 0) {
      toast({
        title: 'No messages to share',
        description: 'Start a conversation first.',
        variant: 'destructive',
      });
      return;
    }

    setIsSharing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: 'Not authenticated',
          description: 'Please sign in to share conversations.',
          variant: 'destructive',
        });
        return;
      }

      const shareToken = generateShareToken();
      const messagesForStorage = messages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        createdAt: msg.createdAt.toISOString(),
      }));

      const { error } = await supabase
        .from('shared_conversations')
        .insert({
          user_id: user.id,
          share_token: shareToken,
          title: conversationTitle || 'AI Assistant Conversation',
          messages: messagesForStorage,
        });

      if (error) throw error;

      const url = `${window.location.origin}/shared/conversation/${shareToken}`;
      setShareUrl(url);
      setShareDialogOpen(true);
    } catch (error) {
      logger.error('Share error:', error);
      toast({
        title: 'Share failed',
        description: 'Could not create share link. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Link copied',
        description: 'Share link has been copied to clipboard.',
      });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Could not copy link.',
        variant: 'destructive',
      });
    }
  };

  const hasMessages = messages.length > 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Export options</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onClick={handleExportPDF}
            disabled={!hasMessages || isExporting}
            className="cursor-pointer"
          >
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export as PDF
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleCopyConversation}
            disabled={!hasMessages}
            className="cursor-pointer"
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy conversation
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleCreateShareLink}
            disabled={!hasMessages || isSharing}
            className="cursor-pointer"
          >
            {isSharing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Share2 className="mr-2 h-4 w-4" />
            )}
            Share via link
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share conversation</DialogTitle>
            <DialogDescription>
              Anyone with this link can view this conversation. The link expires in 30 days.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={shareUrl}
                readOnly
                className="pl-9 pr-4 text-sm"
              />
            </div>
            <Button onClick={handleCopyShareLink} size="sm" className="shrink-0">
              {copied ? (
                <>
                  <Check className="mr-1.5 h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-4 w-4" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
